import { useList, useTranslate } from "@refinedev/core";
import { useMemo } from "react";
import { useSession } from "hooks/useSession";
import type { ContactResponse } from "pages/media/types";
import {
  getProfileNameWithCategory,
  getVoicePreviewForLanguage,
} from "pages/media/components/ContactWithPresenter/helpers";
import {
  emotionCapableContacts,
  voiceProfileId,
} from "../../../helpers/contactVoices";
// TODO: not in the Podcast/AudioDoc API contracts — restore alongside the
// commented format/voice/language option lists below.
// import { formatLanguageName } from "../../../helpers";

/**
 * Contacts → voice options. The narrator is picked as an avatar (contact), but
 * the API field is the avatar's VOICE, so the option value is
 * `presenter.voice_profile.id` — binding straight to
 * `generation_settings.voice_profile_id(s)`.
 *
 * Deduped by that id: several contacts can share one voice profile, and since
 * the value IS the voice, a repeat is the same choice offered twice. It also
 * broke the dropdown — antd keys options by value, so duplicates collided as
 * React keys and rows duplicated/vanished while scrolling the virtual list.
 * The first contact carrying a voice names it.
 *
 * The label names the VOICE, not the contact (a contact's own name is
 * whatever the org typed in when creating it, e.g. "gg" or "11" — not
 * something a listener picking a podcast narrator should have to parse). The
 * voice's own `name` follows a "<Name> - <blurb>" convention (e.g. "Cinnamon -
 * Smart & Plucky Narrator"); `getProfileNameWithCategory` strips the blurb.
 * The tone shown in parentheses comes from the voice's own free-form
 * `tags.description` (e.g. "confident", "calm") — there's no fixed enum for
 * it, so it's just displayed as-is when present.
 *
 * `previewUrl` is the voice's original-language sample (null when the voice
 * has none), so a picker can offer a play button before committing to it.
 */
export interface VoiceOption {
  value: string;
  label: string;
  previewUrl: string | null;
}

export const toVoiceOptions = (
  contacts: ContactResponse[] | undefined
): VoiceOption[] => {
  const seen = new Set<string>();
  const options: VoiceOption[] = [];
  for (const contact of contacts ?? []) {
    const value = voiceProfileId(contact);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    const voiceProfile = contact.presenter.voice_profile;
    const name =
      getProfileNameWithCategory(voiceProfile.name) ||
      `${contact.firstname} ${contact.lastname}`.trim() ||
      contact.email ||
      contact.id;
    const description = voiceProfile.tags?.description;
    options.push({
      value,
      label: description ? `${name} (${description})` : name,
      previewUrl: getVoicePreviewForLanguage(voiceProfile),
    });
  }
  return options;
};

export const useAudioConfigData = () => {
  const t = useTranslate();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;

  const { data: contactsData, isLoading: avatarsLoading } =
    useList<ContactResponse>({
      resource: `media/${organizationId}/contacts`,
      queryOptions: { enabled: !!organizationId },
    });

  const avatarOptions = useMemo(
    () => toVoiceOptions(contactsData?.data),
    [contactsData?.data]
  );

  // Podcast-only subset — see emotionCapableContacts for why AudioDoc keeps
  // the full list.
  const emotionCapableAvatarOptions = useMemo(
    () => toVoiceOptions(emotionCapableContacts(contactsData?.data ?? [])),
    [contactsData?.data]
  );
  // TODO: not in the Podcast API contract (PodcastGenerationSettings /
  // PodcastFunctionalSettings) — restore if the backend adds format / voice
  // mood fields.
  // const formatOptions = [
  //   {
  //     value: "podcast",
  //     label: t("createExperience.addOns.podcast.format.podcast"),
  //   },
  //   {
  //     value: "narration",
  //     label: t("createExperience.addOns.podcast.format.narration"),
  //   },
  // ];

  // const voiceOptions = [
  //   { value: "warm", label: t("createExperience.addOns.podcast.voice.warm") },
  //   {
  //     value: "neutral",
  //     label: t("createExperience.addOns.podcast.voice.neutral"),
  //   },
  //   {
  //     value: "bright",
  //     label: t("createExperience.addOns.podcast.voice.bright"),
  //   },
  //   {
  //     value: "deep",
  //     label: t("createExperience.addOns.podcast.voice.deep"),
  //   },
  // ];

  const readingSpeedOptions = [
    {
      value: "Normal",
      label: t("createExperience.addOns.audioDoc.readingSpeed.normal"),
    },
    {
      value: "Slow",
      label: t("createExperience.addOns.audioDoc.readingSpeed.slow"),
    },
    {
      value: "Fast",
      label: t("createExperience.addOns.audioDoc.readingSpeed.fast"),
    },
  ];

  // TODO: not in the AudioDoc API contract (AudioDocGenerationSettings /
  // AudioDocFunctionalSettings) — restore if the backend adds an intonation
  // field.
  // const intonationOptions = [
  //   {
  //     value: "Natural",
  //     label: t("createExperience.addOns.audioDoc.intonation.natural"),
  //   },
  //   {
  //     value: "Expressive",
  //     label: t("createExperience.addOns.audioDoc.intonation.expressive"),
  //   },
  //   {
  //     value: "Neutral",
  //     label: t("createExperience.addOns.audioDoc.intonation.neutral"),
  //   },
  // ];

  // TODO: not in the Podcast/AudioDoc API contracts — narration language is
  // decided server-side. Restore if the backend adds a language field.
  // const languagesOptions = useMemo(
  //   () =>
  //     languages.map(({ value }) => ({
  //       value,
  //       label: formatLanguageName(value, t),
  //     })),
  //   [t]
  // );

  return {
    readingSpeedOptions,
    avatarOptions,
    emotionCapableAvatarOptions,
    avatarsLoading,
  };
};
